import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { searchService } from '../services/api'
import { useTheme } from '../contexts/ThemeContext'
import SearchBar from '../components/SearchBar'
import LanguageSelector from '../components/LanguageSelector'
import ThemeSwitcher from '../components/ThemeSwitcher'
import LiquidGlassButton from '../components/LiquidGlassButton'
import AppDock from '../components/AppDock'
import { useTranslation } from '../hooks/useTranslation'
import { useAuth } from '../contexts/AuthContext'
import { sanitizeUrl } from '../utils/sanitize'
import { paidApiLimitMessage } from '../utils/paidApiLimit.js'
import { useProductCardImage } from '../hooks/useProductCardImage'
import './SearchPage.css'

function SearchResultImage({ item }) {
  const { src, onError } = useProductCardImage(item)
  if (!src) return null
  return (
    <img
      src={src}
      alt={item.name}
      className="result-image"
      onError={onError}
    />
  )
}

function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { logout } = useAuth() || { logout: () => {} }
  const { theme } = useTheme()
  const { t } = useTranslation()
  const query = searchParams.get('q') || ''
  const [searchTerm, setSearchTerm] = useState(query)
  const [searchMode, setSearchMode] = useState('semantic') // 'classic' ou 'semantic'
  const [isGeneratingEmbeddings, setIsGeneratingEmbeddings] = useState(false)
  const [embeddingStatus, setEmbeddingStatus] = useState(null)

  // Requête pour recherche classique
  const { data: classicResults = { carts: [], items: [], saved_items: [] }, isLoading: isLoadingClassic, refetch: refetchClassic } = useQuery({
    queryKey: ['global-search', query],
    queryFn: () => searchService.global(query),
    enabled: query.length > 0 && searchMode === 'classic',
    staleTime: 0,
  })

  // Requête pour recherche sémantique
  // Seuil réduit à 0.3 pour être plus permissif (0.5 était trop strict)
  const { data: semanticResults = { results: { items: [], saved_items: [] }, count: 0 }, isLoading: isLoadingSemantic, refetch: refetchSemantic } = useQuery({
    queryKey: ['semantic-search', query],
    queryFn: () => searchService.semantic(query, 0.3, 20),
    enabled: query.length > 0 && searchMode === 'semantic',
    staleTime: 0,
  })

  const refetch = searchMode === 'semantic' ? refetchSemantic : refetchClassic

  const isLoading = searchMode === 'semantic' ? isLoadingSemantic : isLoadingClassic

  // Adapter les résultats selon le mode
  const results = searchMode === 'semantic' 
    ? { 
        items: semanticResults.results?.items || [],
        saved_items: semanticResults.results?.saved_items || [],
        carts: [] // La recherche sémantique ne recherche pas dans les paniers pour l'instant
      }
    : classicResults

  useEffect(() => {
    setSearchTerm(query)
  }, [query])

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value)
  }

  const handleSearch = (e) => {
    e.preventDefault()
    const trimmedQuery = searchTerm.trim()
    if (trimmedQuery) {
      setSearchParams({ q: trimmedQuery })
    } else {
      setSearchParams({})
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSearch(e)
    }
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(price || 0)
  }

  const handleGenerateEmbeddings = async () => {
    setIsGeneratingEmbeddings(true)
    setEmbeddingStatus(null)
    try {
      const result = await searchService.generateAllEmbeddings()
      setEmbeddingStatus({
        success: true,
        message: `Embeddings générés : ${result.generated} produits traités (${result.skipped} déjà existants, ${result.errors} erreurs)`
      })
      // Recharger les résultats si on a une recherche active
      if (query.length > 0 && searchMode === 'semantic') {
        refetch()
      }
    } catch (error) {
      setEmbeddingStatus({
        success: false,
        message: paidApiLimitMessage(error) || error.response?.data?.detail || 'Erreur lors de la génération des embeddings'
      })
    } finally {
      setIsGeneratingEmbeddings(false)
    }
  }

  return (
    <div className="search-page">
      <svg style={{ display: 'none' }}>
        <filter id="glass-distortion">
          <feTurbulence type="turbulence" baseFrequency="0.008" numOctaves="2" result="noise" />
          <feDisplacementMap in="SourceGraphic" in2="noise" scale="77" />
        </filter>
        <filter id="lg-dist" x="0%" y="0%" width="100%" height="100%">
          <feTurbulence type="fractalNoise" baseFrequency="0.008 0.008" numOctaves="2" seed="92" result="noise" />
          <feGaussianBlur in="noise" stdDeviation="2" result="blurred" />
          <feDisplacementMap in="SourceGraphic" in2="blurred" scale="70" xChannelSelector="R" yChannelSelector="G" />
        </filter>
      </svg>

      <header className="search-header">
        <div className="header-container">
          <div className="header-left-section">
            <LiquidGlassButton 
              onClick={() => navigate('/carts', { state: { activeApp: 'carts' } })}
              className="btn-back-icon"
              aria-label={t('back')}
            >
              <img src="/Icon house.png" alt={t('back')} />
            </LiquidGlassButton>
            <ThemeSwitcher />
          </div>
          <div className="header-search-section">
            <form onSubmit={handleSearch} className="search-form-container">
              <SearchBar
                placeholder={t('globalSearch') || 'Rechercher'}
                value={searchTerm}
                onChange={handleSearchChange}
                globalSearch={false}
                onKeyDown={handleKeyDown}
              />
            </form>
            <div className="header-right-section">
              <LanguageSelector position="right" />
              <LiquidGlassButton 
                onClick={logout}
                className="btn-logout-icon"
                aria-label="Déconnexion"
              >
                <img src="/Icon logout.png" alt="Déconnexion" />
              </LiquidGlassButton>
            </div>
          </div>
        </div>
      </header>

      <div className="search-content">
        {/* Message si aucun résultat en recherche sémantique */}
        {query.length > 0 && searchMode === 'semantic' && !isLoading && results.items.length === 0 && results.saved_items.length === 0 && (
          <div className="no-embeddings-warning">
            <p>⚠️ Aucun résultat trouvé. Les produits existants n'ont peut-être pas d'embeddings générés.</p>
            <div className="button-wrap">
              <div className="button-shadow"></div>
              <button 
                onClick={handleGenerateEmbeddings}
                disabled={isGeneratingEmbeddings}
                className="interpret-data-btn"
              >
                <span>{isGeneratingEmbeddings ? 'Interprétation en cours...' : 'Interpréter les données'}</span>
              </button>
            </div>
            {embeddingStatus && (
              <p className={embeddingStatus.success ? 'embedding-success' : 'embedding-error'}>
                {embeddingStatus.message}
              </p>
            )}
          </div>
        )}

        {/* Sélecteur de mode de recherche */}
        {query.length > 0 && (
          <div className="search-mode-selector">
            <button
              className={`mode-button ${searchMode === 'semantic' ? 'active' : ''}`}
              onClick={() => setSearchMode('semantic')}
              type="button"
            >
              <span className="mode-icon">
                <img src="/Icon auto awesome.png" alt="Recherche Sémantique" style={{ width: '24px', height: '24px' }} />
              </span>
              <div>
                <div className="mode-title">Recherche Sémantique</div>
                <div className="mode-description">Interprète le rapport sémantique grâce à notre modèle intelligent PioumonInterpreter V.1.0.0.</div>
              </div>
            </button>
            <button
              className={`mode-button ${searchMode === 'classic' ? 'active' : ''}`}
              onClick={() => setSearchMode('classic')}
              type="button"
            >
              <span className="mode-icon">
                <img src="/Icon search.png" alt="Recherche Classique" style={{ width: '24px', height: '24px' }} />
              </span>
              <div>
                <div className="mode-title">Recherche Classique</div>
                <div className="mode-description">Recherche par mots-clés exacts</div>
              </div>
            </button>
          </div>
        )}

        {isLoading ? (
          <p>{t('searching')}...</p>
        ) : query.length === 0 ? (
          <div className="no-query-message">
            <p>{t('enterSearchQuery')}. {t('searchInAllCarts')}.</p>
            <p className="semantic-hint">
              💡 <strong>Recherche Sémantique :</strong> Comprend le sens de vos recherches, pas seulement les mots exacts.
              <br />
              <strong>Exemples :</strong>
              <br />
              • "PC" trouve aussi "ordinateur", "laptop", "portable"
              <br />
              • "écouteurs" trouve aussi "casque", "headphones"
              <br />
              • "micro" trouve aussi "microphone", "micro-ondes"
              <br />
              • "table" trouve aussi "bureau", "table de cuisine"
            </p>
            <div style={{ marginTop: '20px' }}>
              <div className="button-wrap">
                <div className="button-shadow"></div>
                <button 
                  onClick={handleGenerateEmbeddings}
                  disabled={isGeneratingEmbeddings}
                  className="interpret-data-btn"
                >
                  <span>{isGeneratingEmbeddings ? 'Interprétation en cours...' : 'Interpréter les données'}</span>
                </button>
              </div>
              {embeddingStatus && (
                <p className={embeddingStatus.success ? 'embedding-success' : 'embedding-error'} style={{ marginTop: '10px' }}>
                  {embeddingStatus.message}
                </p>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Afficher le nombre de résultats pour recherche sémantique */}
            {searchMode === 'semantic' && semanticResults.count > 0 && (
              <p className="search-info">
                {semanticResults.count} résultat(s) trouvé(s) pour "{query}"
              </p>
            )}

            {results.carts.length > 0 && (
              <section className="search-results-section">
                <h2>{t('carts')} ({results.carts.length})</h2>
                <div className="results-grid">
                  {results.carts.map(cart => (
                    <div key={cart.id} className="glass-card search-result-card" onClick={() => navigate(`/carts/${cart.id}`)}>
                      <h3>{cart.name}</h3>
                      <p>{t('total')} : {formatPrice(cart.total)}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {results.items.length > 0 && (
              <section className="search-results-section">
                <h2>{t('products')} ({results.items.length})</h2>
                <div className="results-grid">
                  {results.items.map(item => (
                    <div key={item.id} className="glass-card search-result-card" onClick={() => navigate(`/carts/${item.cart_id}`)}>
                      {/* Afficher le score de similarité pour recherche sémantique */}
                      {searchMode === 'semantic' && item.similarity !== undefined && (
                        <div className="similarity-badge">
                          {(item.similarity * 100).toFixed(0)}% de similarité
                        </div>
                      )}
                      <SearchResultImage item={item} />
                      <h3>{item.name}</h3>
                      <p>{t('price')} : {formatPrice(item.price)}</p>
                      <p>{t('inCart')} : {item.cart_name}</p>
                      {sanitizeUrl(item.link) && <a href={sanitizeUrl(item.link)} target="_blank" rel="noopener noreferrer" className="result-link">{t('viewLink')}</a>}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {results.saved_items.length > 0 && (
              <section className="search-results-section">
                <h2>{t('library')} ({results.saved_items.length})</h2>
                <div className="results-grid">
                  {results.saved_items.map(item => (
                    <div key={item.id} className="glass-card search-result-card" onClick={() => navigate(`/library`)}>
                      {/* Afficher le score de similarité pour recherche sémantique */}
                      {searchMode === 'semantic' && item.similarity !== undefined && (
                        <div className="similarity-badge">
                          {(item.similarity * 100).toFixed(0)}% de similarité
                        </div>
                      )}
                      <SearchResultImage item={item} />
                      <h3>{item.name}</h3>
                      <p>{t('price')} : {formatPrice(item.price)}</p>
                      <p>{t('inLibrary')}</p>
                      {sanitizeUrl(item.link) && <a href={sanitizeUrl(item.link)} target="_blank" rel="noopener noreferrer" className="result-link">{t('viewLink')}</a>}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {results.carts.length === 0 && results.items.length === 0 && results.saved_items.length === 0 && (
              <p className="no-results-message">{t('noResults')}. {t('tryDifferentQuery')}.</p>
            )}
          </>
        )}
      </div>

      {/* Dock de navigation */}
      <AppDock 
        onAppSelect={(appId) => {
          if (appId === 'house') {
            navigate('/carts', { state: { activeApp: 'house' } })
          } else if (appId === 'carts') {
            navigate('/carts', { state: { activeApp: 'carts' } })
          } else if (appId === 'analytics') {
            navigate('/carts', { state: { activeApp: 'analytics' } })
          } else if (appId === 'social') {
            navigate('/carts', { state: { activeApp: 'social' } })
          } else if (appId === 'settings') {
            navigate('/carts', { state: { activeApp: 'settings' } })
          }
        }} 
        selectedApp={null}
      />
    </div>
  )
}

export default SearchPage
